import {
  getStreamCollectionInitialState,
  StreamCollection,
  toStreamCollection,
} from 'streams/utils/streamUtils';
import {
  APIWorkspaceAttributes,
  ArdoqId,
  ResourceType,
} from '@ardoq/api-types';
import {
  reducedStream,
  reducer,
  streamReducer,
  toPersistentStream,
} from '@ardoq/rxbeach';
import { filterResourceType, websocket$ } from 'sync/websocket$';

import { map, distinctUntilChanged, skip } from 'rxjs/operators';
import { fetchAllReducer, websocketReducer } from 'streams/crud/reducers';
import { updateEntity } from '@ardoq/common-helpers';
import { uniqBy } from 'lodash';

import * as workspaceActions from './actions';

export type WorkspacesState = StreamCollection<APIWorkspaceAttributes>;

export const resetWorkspaces = (
  _: APIWorkspaceAttributes[],
  workspaces: APIWorkspaceAttributes[]
): APIWorkspaceAttributes[] => workspaces;

export const addWorkspaces = (
  state: APIWorkspaceAttributes[],
  { workspaces }: workspaceActions.AddWorkspacesPayload
): APIWorkspaceAttributes[] => [
  ...new Map<string, APIWorkspaceAttributes>(
    [...state, ...workspaces].map(workspace => [workspace._id, workspace])
  ).values(),
];

export const replaceWorkspaces = (
  _: APIWorkspaceAttributes[],
  { workspaces }: workspaceActions.AddWorkspacesPayload
): APIWorkspaceAttributes[] => uniqBy(workspaces, '_id');

export const clearWorkspaces = (
  _: APIWorkspaceAttributes[]
): APIWorkspaceAttributes[] => [];

export const removeWorkspaces = (
  state: APIWorkspaceAttributes[],
  ids: ArdoqId[]
): APIWorkspaceAttributes[] => {
  const idsSet = new Set(ids);
  return state.filter(m => !idsSet.has(m._id));
};

const workspaces$ = reducedStream<APIWorkspaceAttributes[]>(
  'workspacesOnly$',
  [],
  [
    fetchAllReducer(resetWorkspaces),
    reducer(workspaceActions.resetWorkspaces, clearWorkspaces),
    reducer(workspaceActions.resetWorkspacesWithData, replaceWorkspaces),
    reducer(workspaceActions.addWorkspaces, addWorkspaces),
    reducer(workspaceActions.removeWorkspaces, removeWorkspaces),
    streamReducer(
      websocket$.pipe(
        filterResourceType<APIWorkspaceAttributes>(ResourceType.WORKSPACE)
      ),
      websocketReducer({
        update: updateEntity,
      })
    ),
  ],
  { namespace: workspaceActions.workspaceNamespace }
);

const persistentWorkspaces$ = toPersistentStream(
  'workspaces$',
  workspaces$.pipe(map(toStreamCollection)),
  getStreamCollectionInitialState()
);
export const workspaceNamesChanged$ = persistentWorkspaces$.pipe(
  map(state =>
    state.models
      .map(ws => `${ws._id}:${ws.name}`)
      .sort()
      .join('|')
  ),
  distinctUntilChanged(),
  skip(1),
  map(() => null)
);

export { persistentWorkspaces$ };
